import { BilateralPnlCellSchema } from "./eex-bilateral-pnl.js";
import { z } from "zod";
export const RecognitionScheduleMonthSchema = z.string().regex(/^(20\d{2}|2100)-(0[1-9]|1[0-2])$/);
const Amount = z.number().finite();
const Hash = z.string().regex(/^[a-f0-9]{64}$/);
// One portfolio operation; monthly slices of a trade are not separate user tasks.
export const RECOGNITION_BATCH_LIMIT = 5000;
export const RecognitionScheduleAllocationSchema = z.object({
    month: RecognitionScheduleMonthSchema,
    percent: z.number().finite().positive().max(100),
}).strict();
export const RecognitionScheduleAllocationsSchema = z.array(RecognitionScheduleAllocationSchema).min(1).max(60)
    .refine((rows) => new Set(rows.map((r) => r.month)).size === rows.length, "Use each month once")
    .refine((rows) => Math.abs(rows.reduce((sum, r) => sum + r.percent, 0) - 100) <= 1e-8, "Allocations must total 100%");
export const RecognitionScheduleDraftSchema = z.object({
    itemId: Hash,
    sourceFingerprint: Hash,
    quantity: z.number().finite().positive().refine((value) => Math.abs(value * 1e6 - Math.round(value * 1e6)) < 0.00001, "Quantity supports up to six decimal places"),
    allocations: RecognitionScheduleAllocationsSchema,
    reason: z.string().trim().min(3).max(2000),
    replacesId: z.string().uuid().nullable().optional(),
}).strict();
export const RecognitionScheduleReasonSchema = z.object({ reason: z.string().trim().min(3).max(2000) }).strict();
const SourceSchema = z.object({
    exitKind: z.enum(["close", "delivery"]),
    recognitionMonth: RecognitionScheduleMonthSchema,
    recognitionDate: z.string(),
    entry: Amount, exit: Amount, volume: Amount, lots: Amount,
    unit: z.string(), nativeCcy: z.string(), nativeAmount: Amount,
    entryDate: z.string().nullable(), exitDate: z.string().nullable(),
    entryTradeId: z.string().nullable(), exitTradeId: z.string().nullable(),
    entrySource: z.string(), exitSource: z.string(),
    side: z.enum(["B", "S"]).nullable().optional(),
    book: z.string().nullable(), trader: z.string().nullable(), pair: z.string().nullable(),
    contract: z.object({ code: z.string(), expYear: z.number().int(), expMonth: z.number().int() }),
    recognizedEUR: Amount.nullable(), fxRate: Amount.nullable(), fxRateDate: z.string().nullable(),
    fxSource: z.string().nullable(), finality: z.enum(["final", "estimate", "stale"]),
});
export const RecognitionScheduleItemSchema = z.object({
    itemId: Hash, sourceFingerprint: Hash, source: SourceSchema,
    quantity: Amount, sourceMonth: RecognitionScheduleMonthSchema, sourceDate: z.string(),
    eligible: z.boolean(), blockers: z.array(z.string()), sourceTradeIds: z.array(z.string()),
});
export const RecognitionScheduleEntrySchema = z.object({
    id: z.string().uuid(), itemId: Hash, sourceFingerprint: Hash,
    quantity: Amount, allocations: z.array(RecognitionScheduleAllocationSchema), reason: z.string(),
    sourceTradeIds: z.array(z.string()), sourceMonth: RecognitionScheduleMonthSchema,
    status: z.enum(["pending", "active", "rejected", "reversed"]),
    replacesId: z.string().uuid().nullable(),
    createdBy: z.string().nullable(), createdAt: z.string(),
    reviewedBy: z.string().nullable(), reviewedAt: z.string().nullable(),
    reversalReason: z.string().nullable(), stale: z.boolean(),
    batchId: z.string().uuid().nullable().optional(),
});
// Read-only portfolio preview. Manual drafts are inert; the current approved
// journal is always the Before baseline. A source result is counted once even
// when both matched execution legs are selected.
export const RecognitionScheduleImpactRequestSchema = z.object({
    scope: z.enum(["pair", "unpaired"]).default("pair"),
    pair: z.string().trim().min(1).max(80).optional(),
    selectedTradeIds: z.array(z.string().uuid()).max(RECOGNITION_BATCH_LIMIT).optional(),
    drafts: z.array(RecognitionScheduleDraftSchema).max(RECOGNITION_BATCH_LIMIT).default([]),
    pendingIds: z.array(z.string().uuid()).max(RECOGNITION_BATCH_LIMIT).default([]),
    basis: z.enum(["current", "automatic"]).optional(),
}).strict().refine((value) => value.scope !== "unpaired" || value.pair === undefined, { message: "Unpaired trades cannot also select a PAIR", path: ["pair"] });
export const RecognitionSchedulePreviewRequestSchema = RecognitionScheduleDraftSchema.extend({ impact: RecognitionScheduleImpactRequestSchema.optional() });
export const RecognitionScheduleApprovalSchema = z.object({ fingerprint: Hash, impact: RecognitionScheduleImpactRequestSchema.optional() }).strict();
export const RecognitionScheduleApplySchema = z.object({ draft: RecognitionScheduleDraftSchema, fingerprint: Hash, impact: RecognitionScheduleImpactRequestSchema.optional() }).strict();
const ImpactAmountsSchema = z.object({
    beforeEUR: Amount.nullable(), changeEUR: Amount.nullable(), afterEUR: Amount.nullable(),
    beforeFinalEUR: Amount.nullable(), changeFinalEUR: Amount.nullable(), afterFinalEUR: Amount.nullable(),
    beforeEstimateEUR: Amount.nullable(), changeEstimateEUR: Amount.nullable(), afterEstimateEUR: Amount.nullable(),
    physicalEstimateEUR: Amount.nullable(), fxMissingCount: z.number().int().nonnegative(),
    bilateral: BilateralPnlCellSchema.optional(),
});
export const RecognitionScheduleImpactSchema = z.object({
    asOf: z.string(), fingerprint: Hash, scope: z.enum(["pair", "unpaired"]), selectedPair: z.string().nullable(),
    selectedTradeIds: z.array(z.string()), sourceTradeIds: z.array(z.string()), sourceItemIds: z.array(Hash),
    rows: z.array(ImpactAmountsSchema.extend({ month: RecognitionScheduleMonthSchema })),
    totals: ImpactAmountsSchema,
    roundingDifference: z.object({ beforeEUR: Amount.nullable(), changeEUR: Amount.nullable(), afterEUR: Amount.nullable() }),
    // Primary amounts/completeness describe cleared financial recognition only.
    // Supplemental physical valuation must never gate a financial schedule.
    blockers: z.array(z.string()), approvalBlockers: z.array(z.string()), complete: z.boolean(),
    physicalComplete: z.boolean().optional(), physicalBlockers: z.array(z.string()).optional(),
    includedDraftItemIds: z.array(Hash), includedPendingIds: z.array(z.string().uuid()),
});
export const RecognitionScheduleWorkspaceSchema = z.object({
    asOf: z.string(), fingerprint: Hash,
    scope: z.enum(["pair", "unpaired"]).default("pair"),
    pairs: z.array(z.object({ name: z.string(), tradeCount: z.number().int().nonnegative() })),
    selectedPair: z.string().nullable(),
    totalTradeCount: z.number().int().nonnegative().optional(),
    focusedTradeId: z.string().uuid().nullable().optional(),
    trades: z.array(z.object({
        id: z.string(), product: z.string(), period: z.string(), side: z.enum(["B", "S"]),
        quantity: Amount, price: Amount, tradedAt: z.string().nullable(),
        currency: z.string(), priceUnit: z.string(), quantityUnit: z.string(),
        contract: z.object({ code: z.string(), expYear: z.number().int(), expMonth: z.number().int() }).nullable(),
        matchedQuantity: Amount.nullable(), openQuantity: Amount.nullable(),
        settlementKind: z.enum(["cleared", "physical", "otc_financial", "unclassified"]).optional(),
        origin: z.enum(["cleared", "bilateral"]), pair: z.string().nullable(),
        book: z.string().nullable(), trader: z.string().nullable(),
    })),
    items: z.array(RecognitionScheduleItemSchema), entries: z.array(RecognitionScheduleEntrySchema),
    pairImpact: z.array(z.object({
        month: RecognitionScheduleMonthSchema,
        defaultEUR: Amount.nullable(), adjustmentEUR: Amount.nullable(), effectiveEUR: Amount.nullable(),
        estimateEUR: Amount.nullable(), fxMissingCount: z.number().int().nonnegative(),
    })).nullable(),
    pairImpactBlockers: z.array(z.string()),
    monthlyImpact: RecognitionScheduleImpactSchema.optional(),
    capabilities: z.object({ canPropose: z.boolean(), canApprove: z.boolean(), writeDisabledReason: z.string().nullable().optional() }),
}).refine((workspace) => workspace.scope !== "unpaired" || workspace.selectedPair === null, { message: "Unpaired trades cannot also select a PAIR", path: ["selectedPair"] });
export const RecognitionSchedulePreviewSchema = z.object({
    asOf: z.string(), fingerprint: Hash, item: RecognitionScheduleItemSchema,
    rows: z.array(z.object({
        month: RecognitionScheduleMonthSchema, state: z.enum(["open", "soft", "hard"]),
        defaultNative: Amount, adjustmentNative: Amount, effectiveNative: Amount,
        defaultEUR: Amount.nullable(), adjustmentEUR: Amount.nullable(), effectiveEUR: Amount.nullable(),
    })),
    impact: RecognitionScheduleImpactSchema.optional(),
    totalNative: Amount, totalEUR: Amount.nullable(), allowed: z.boolean(), blockers: z.array(z.string()),
});
// Bulk changes share a reviewed snapshot, but each result retains its own
// schedule and audit trail. No draft may be silently dropped from the scope.
const BatchRequestFields = {
    scope: z.enum(["pair", "unpaired"]).default("pair"),
    pair: z.string().trim().min(1).max(80).optional(),
    selectedTradeIds: z.array(z.string().uuid()).max(RECOGNITION_BATCH_LIMIT).optional(),
    drafts: z.array(RecognitionScheduleDraftSchema).min(1).max(RECOGNITION_BATCH_LIMIT)
        .refine((drafts) => new Set(drafts.map((draft) => draft.itemId)).size === drafts.length, "Choose each recognition result once"),
};
const validBatchScope = (value) => value.scope === "unpaired" ? value.pair === undefined : value.pair !== undefined;
const batchScopeError = { message: "Choose a PAIR or the unpaired scope", path: ["pair"] };
export const RecognitionScheduleBatchPreviewRequestSchema = z.object(BatchRequestFields).strict().refine(validBatchScope, batchScopeError);
export const RecognitionScheduleBatchApplySchema = z.object({ ...BatchRequestFields, fingerprint: Hash }).strict().refine(validBatchScope, batchScopeError);
export const RecognitionScheduleBatchPreviewSchema = z.object({
    fingerprint: Hash, allowed: z.boolean(), blockers: z.array(z.string()),
    previews: z.array(RecognitionSchedulePreviewSchema).min(1).max(RECOGNITION_BATCH_LIMIT),
    impact: RecognitionScheduleImpactSchema,
});
export const RecognitionScheduleBatchMutationResponseSchema = z.object({
    schedules: z.array(z.object({ id: z.string().uuid(), status: z.literal("active") })).min(1).max(RECOGNITION_BATCH_LIMIT),
    batchId: z.string().uuid().optional(),
    impact: RecognitionScheduleImpactSchema,
});
export const RecognitionScheduleBatchReverseSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(RECOGNITION_BATCH_LIMIT).refine((ids) => new Set(ids).size === ids.length, "Choose each schedule once"),
    reason: z.string().trim().min(3).max(2000),
}).strict();
export const RecognitionScheduleBatchReverseResponseSchema = z.object({ ids: z.array(z.string().uuid()), status: z.literal("reversed") });
export const RecognitionScheduleMutationResponseSchema = z.object({ id: z.string().uuid(), status: z.enum(["pending", "active", "rejected", "reversed"]), impact: RecognitionScheduleImpactSchema.optional() });
//# sourceMappingURL=eex-recognition-schedule-contracts.js.map